import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2, ChevronDown, X, Plus, Book, Video, FileText, Eye } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { Modal } from "antd";
import CoursesModal from "../../modals/admin/CoursesModal";
import ExamViewerEditorModal from "../../modals/admin/ExamViewerEditorModal";
import {
  fetchCourses,
  addCourse,
  updateCourse,
  deleteCourse,
  setPageNumber,
} from "../../redux/coursesSlice";
import { renderStars, uploadDocuments, uploadImage, uploadVideos } from "../../utils";
import { useTranslation } from "react-i18next";
const Courses = () => {
  const dispatch = useDispatch();
  const { courses, loading, pageNumber, pageSize, totalCount } = useSelector(
    (state) => state.courses
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [expandedCourseIds, setExpandedCourseIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isExamModalOpen, setIsExamModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for tracking form submission
  const { t, i18n } = useTranslation();
  
  useEffect(() => {
    dispatch(fetchCourses({ pageNumber, pageSize }));
  }, [dispatch, pageNumber, pageSize]);

  const onSubmit = async (courseData, coverImage, video) => {
    setIsSubmitting(true); // Set loading state to true when submission starts
    
    try {
      let coverImageUrl = null;
      let videoUrl = null;
    
      if (coverImage) {
        coverImageUrl = await uploadImage(coverImage);
      } else if (isEdit && currentCourse) {
        coverImageUrl = currentCourse?.coverImageUrl;
      }
    
      if (video) {
        videoUrl = (await uploadVideos([video]))?.[0];
      } else if (isEdit && currentCourse) {
        videoUrl = currentCourse?.videoUrl;
      }
    
      const uploadFile = async (file, type) => {
        switch (type) {
          case "Document":
            return (await uploadDocuments([file]))?.[0];
          case "Video":
            return (await uploadVideos([file]))?.[0];
          case "Image":
          case "Project":
            return await uploadImage(file);
          default:
            return "";
        }
      };
    
      const plans = await Promise.all(
        (courseData.plans || []).map(async (week, weekIndex) => ({
          id: isEdit && week.id ? week.id : undefined,
          weekName: week.weekName || `Week ${weekIndex + 1}`,
          activities: await Promise.all(
            (week.activities || []).map(async (activity) => {
              let fileValue = "";
              
              if (Array.isArray(activity.file) && activity.file.length > 0 && activity.file[0] instanceof File) {
                fileValue = await uploadFile(activity.file[0], activity.activityType);
              } 
              else if (typeof activity.file === 'string' && activity.file && activity.activityType !== "Exam") {
                fileValue = activity.file;
              }
              
              return {
                id: isEdit && activity.id ? activity.id : undefined,
                activityName: activity.activityName,
                activityType: activity.activityType,
                file: fileValue,
                activityDescription: activity.description,
                examDetails: activity.examDetails || null
              };
            })
          ),
        }))
      );
    
      const payload = {
        name: courseData.name,
        type: courseData.type,
        creditHours: courseData.creditHours,
        startedSession: courseData.startedSession,
        endSession: courseData.endSession,
        actualPrice: courseData.actualPrice,
        discountedPrice: courseData.discountedPrice,
        description: courseData.description,
        coverImageUrl,
        videoUrl,
        plans,
      };
    
      if (isEdit && currentCourse) {
        await dispatch(updateCourse({ id: currentCourse.id, payload }));
      } else {
        await dispatch(addCourse(payload));
      }
    
      toast.success(isEdit ? "Course updated successfully!" : "Course added successfully!");
      setIsModalOpen(false);
      setCurrentCourse(null);
      setIsEdit(false);
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Error submitting course:", error);
    } finally {
      setIsSubmitting(false); // Set loading state back to false when submission ends
    }
  };

  const handleEditCourse = (course) => {
    setCurrentCourse(course);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDeleteCourse = async (id) => {
    await dispatch(deleteCourse(id));
    toast.success("Course deleted successfully!");
  };

  const toggleExpand = (id) => {
    setExpandedCourseIds((prevIds) =>
      prevIds.includes(id)
        ? prevIds.filter((courseId) => courseId !== id)
        : [...prevIds, id]
    );
  };

  const handleViewExam = (courseId, weekIndex, activity) => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return;

    setSelectedCourseId(courseId);
    setSelectedWeekIndex(weekIndex);
    
    const activityIndex = course.plans[weekIndex].activities.findIndex(
      a => a.activityName === activity.activityName
    );
    setSelectedActivityIndex(activityIndex);
    
    setSelectedExam(activity.courseExam);
    setIsExamModalOpen(true);
  };

  const handleExamUpdate = (updatedExam) => {
    const courseToUpdate = courses.find(c => c.id === selectedCourseId);
    if (!courseToUpdate) return;
    
    const updatedCourse = JSON.parse(JSON.stringify(courseToUpdate));
    
    updatedCourse.plans[selectedWeekIndex].activities[selectedActivityIndex].courseExam = updatedExam;
    
    dispatch(updateCourse({ 
      id: selectedCourseId, 
      payload: updatedCourse 
    }));
    
    toast.success("Exam updated successfully!");
    setIsExamModalOpen(false);
  };

  const filteredCourses = courses.filter((course) =>
    [course.name, course.type].some((field) =>
      field?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const renderActivityIcon = (activityType) => {
    switch (activityType) {
      case "Document":
        return <FileText size={16} className="mr-2 text-blue-500" />;
      case "Video":
        return <Video size={16} className="mr-2 text-red-500" />;
      case "Exam":
        return <Book size={16} className="mr-2 text-purple-500" />;
      default:
        return null;
    }
  };

  const renderExamDetails = (exam, courseId, weekIndex, activity) => {
    if (!exam) return null;
    
    return (
      <div className="mt-2 pl-4 border-l-2 border-purple-300">
        <div className="flex justify-between items-center mb-1">
          <div className="text-sm font-medium text-purple-700">{exam.title}</div>
          <button 
            onClick={() => handleViewExam(courseId, weekIndex, activity)}
            className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center"
          >
            <Eye size={14} className="mr-1" /> View Details
          </button>
        </div>
        {exam.description && <div className="text-sm text-gray-600 mb-2">{exam.description}</div>}
        
        {exam.type === "MCQ" && exam.mcqs && exam.mcqs.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">
              MCQs: {exam.mcqs.length} questions
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
              <div className="text-gray-600">Total Marks: {exam.mcqs.reduce((sum, q) => sum + q.marks, 0)}</div>
              <div className="text-gray-600">
                Time: {Math.floor(exam.mcqs.reduce((sum, q) => sum + q.time, 0) / 60)} min
              </div>
            </div>
          </div>
        )}
        
        {exam.type === "QA" && exam.questionAnswers && exam.questionAnswers.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">
              Q&A: {exam.questionAnswers.length} questions
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
              <div className="text-gray-600">Total Marks: {exam.questionAnswers.reduce((sum, q) => sum + q.marks, 0)}</div>
              <div className="text-gray-600">
                Time: {Math.floor(exam.questionAnswers.reduce((sum, q) => sum + q.time, 0) / 60)} min
              </div>
            </div>
          </div>
        )}
        
        {(exam.type === "BOTH" || exam.type === "Written") && (
          <div>
            {exam.mcqs && exam.mcqs.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1">
                  MCQs: {exam.mcqs.length} questions
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  <div className="text-gray-600">Total Marks: {exam.mcqs.reduce((sum, q) => sum + q.marks, 0)}</div>
                  <div className="text-gray-600">
                    Time: {Math.floor(exam.mcqs.reduce((sum, q) => sum + q.time, 0) / 60)} min
                  </div>
                </div>
              </div>
            )}
            {exam.questionAnswers && exam.questionAnswers.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-gray-500 mb-1">
                  Q&A: {exam.questionAnswers.length} questions
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  <div className="text-gray-600">Total Marks: {exam.questionAnswers.reduce((sum, q) => sum + q.marks, 0)}</div>
                  <div className="text-gray-600">
                    Time: {Math.floor(exam.questionAnswers.reduce((sum, q) => sum + q.time, 0) / 60)} min
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const coursesToDisplay = filteredCourses;
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentCourses = coursesToDisplay;

  return (
    <div className="container mx-auto p-6 border border-[#241763]">
      <h2 className="text-lg font-semibold mb-4">
        Total Courses: {filteredCourses.length}
      </h2>

      <div className="flex justify-between mb-4 space-x-4">
        <input
          type="text"
          placeholder="Search by Course Name or Type"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-3/4 p-2 border rounded"
        />
        <button
          className="flex items-center justify-center w-1/4 bg-[#241763] text-white p-2 rounded space-x-2"
          onClick={() => {
            setIsModalOpen(true);
            setCurrentCourse(null);
            setIsEdit(false);
          }}
        >
          <Plus className="h-4 w-4" />
          <span>Add Course</span>
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-end">
              <button
                className="bg-red-500 text-white rounded p-2"
                onClick={() => {
                  if (!isSubmitting) {
                    setIsModalOpen(false);
                  }
                }}
                disabled={isSubmitting}
              >
                <X />
              </button>
            </div>
            {isSubmitting ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#241763]"></div>
                <p className="mt-4 text-lg font-medium text-[#241763]">
                  {isEdit ? "Updating course..." : "Adding course..."}
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  This may take a moment while we upload files and process your request.
                </p>
              </div>
            ) : (
              <CoursesModal
               currentClass={currentCourse} 
               onSubmit={onSubmit}
               isEdit={isEdit}
              />
            )}
          </div>
        </div>
      )}

      {isExamModalOpen && selectedExam && (
        <Modal
          open={isExamModalOpen}
          onCancel={() => setIsExamModalOpen(false)}
          footer={null}
          width={800}
        >
          <ExamViewerEditorModal
            exam={selectedExam}
            onClose={() => setIsExamModalOpen(false)}
            onEdit={handleExamUpdate}
          />
        </Modal>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#241763]"></div>
          <p className="mt-2">Loading courses...</p>
        </div>
      ) : (
        <table className="table-auto w-full mt-6">
        <thead className="py-4 border-b font-bold">
          <tr>
            <th className="p-2">Cover Image</th>
            <th className="p-2">Course Name</th>
            <th className="p-2">Course Type</th>
            <th className="p-2">Rating</th>
            <th className="p-2">Actual Price</th>
            <th className="p-2">Discounted Price</th>
            <th className="p-2">Video</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {currentCourses.map((course) => (
            <React.Fragment key={course.id}>
              <tr className="hover:bg-gray-200">
                <td className="p-2">
                  {course.coverImageUrl ? (
                    <img src={course.coverImageUrl} alt="Course cover" className="w-8 h-8 sm:w-10 sm:h-10 rounded cursor-pointer" />
                  ) : (
                    "No Image"
                  )}
                </td>
                <td className="p-2">{course.name}</td>
                <td className="p-2">{course.type}</td>
                <td>
                  <div className="flex items-center">{renderStars(course.rating)}</div>
                </td>
                <td className="p-2">${course.price || course.actualPrice || "N/A"}</td>
                <td className="p-2">${course.discountedPrice || "N/A"}</td>
                <td className="p-2">
                  {course.videoUrl ? (
                    <a
                      href={course.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      <Video size={16} className="mr-1" /> View
                    </a>
                  ) : (
                    "No Video"
                  )}
                </td>
                <td className="p-2 space-x-2 flex items-center">
                  <button
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    onClick={() => handleEditCourse(course)}
                  >
                    <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    onClick={() => handleDeleteCourse(course.id)}
                  >
                    <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    onClick={() => toggleExpand(course.id)}
                  >
                    <ChevronDown
                      className={`w-4 h-4 text-[#241763] group-hover:text-white transition-transform ${
                        expandedCourseIds.includes(course.id) ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </td>
              </tr>
              {expandedCourseIds.includes(course.id) && (
                <tr>
                  <td colSpan="8" className="p-4 bg-gray-100">
                    <div className="mb-4">
                      <h3 className="font-bold text-lg mb-2 text-[#241763]">{t("CourseDetails")}</h3>
                      <p className="text-gray-700">{course.description || "No description available."}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="font-bold text-lg mb-2 text-[#241763]">{t("CoursePlans")}</h3>
                      {course.plans && course.plans.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {course.plans.map((plan, weekIndex) => (
                            <div
                              key={weekIndex}
                              className="border border-gray-300 shadow-md rounded-lg p-4 bg-white"
                            >
                              <h5 className="font-bold text-lg mb-2 text-[#241763]">{plan.weekName}</h5>
                              {plan.activities && plan.activities.length > 0 ? (
                                <ul className="space-y-2">
                                  {plan.activities.map((activity, idx) => (
                                    <li key={idx} className="border-b border-gray-200 pb-2 last:border-b-0">
                                      <div className="flex items-center">
                                        {renderActivityIcon(activity.activityType)}
                                        <span className="font-medium text-gray-800">
                                          {activity.activityName}
                                        </span>
                                        <span className="ml-2 text-xs px-2 py-1 bg-gray-200 rounded-full">
                                          {activity.activityType}
                                        </span>
                                      </div>
                                      
                                      {activity.filePath && activity.activityType !== "Exam" && (
                                        <a
                                          href={activity.filePath}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="ml-6 text-blue-600 hover:underline text-sm inline-flex items-center mt-1"
                                        >
                                          <FileText size={14} className="mr-1" /> View Resource
                                        </a>
                                      )}
                                      
                                      {activity.activityType === "Exam" && activity.courseExam && 
                                        renderExamDetails(activity.courseExam, course.id, weekIndex, activity)
                                      }
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-500 text-center py-4">No activities listed for this week.</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          No plans added yet for this course.
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      
      )}
      <Toaster />
    </div>
  );
};

export default Courses;